import { DiscoveryApi, FetchApi, OAuthApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  ApproveOrMergeResult,
  ComponentPrsResponse,
  GroupPrs,
  ListPrsByGroupOptions,
  ListPrsOptions,
  MergeOpsApi,
} from './MergeOpsApi';
import {
  BuildStatus,
  DeployStatus,
  GroupE2eResults,
  PrFileDiff,
  PrSummary,
  TerraformPlan,
} from './types';

// Build a `?a=1&b=2` suffix, skipping undefined/empty values. Returns '' when
// there's nothing to add so callers can append it unconditionally.
function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

const comp = (name: string) => `/components/${encodeURIComponent(name)}`;
const group = (name: string) => `/groups/${encodeURIComponent(name)}`;

export class MergeOpsClient implements MergeOpsApi {
  constructor(
    private readonly options: {
      discoveryApi: DiscoveryApi;
      fetchApi: FetchApi;
      githubAuth: OAuthApi;
    },
  ) {}

  // Single place for base-url join + fetch + ok-check + JSON parse, so each
  // endpoint method is just URL-building + one call.
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const base = await this.options.discoveryApi.getBaseUrl('mergeops');
    const res = await this.options.fetchApi.fetch(`${base}${path}`, init);
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return (await res.json()) as T;
  }

  // Plain JSON POST (bot-side actions: cherry-pick / promote).
  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  // POST that runs as the signed-in user: attaches their GitHub token so the
  // backend performs approve/merge under their identity. `workflow` (on top of
  // `repo`) is required to merge PRs touching `.github/workflows/**`; without it
  // GitHub rejects the merge with 403 "workflows scope may be required". The
  // first call triggers the GitHub OAuth consent popup.
  private async postAsUser<T>(path: string, body?: unknown): Promise<T> {
    const token = await this.options.githubAuth.getAccessToken([
      'repo',
      'workflow',
    ]);
    return this.request<T>(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-github-token': token,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  listPrs(opts: ListPrsOptions): Promise<ComponentPrsResponse> {
    return this.request<ComponentPrsResponse>(
      `${comp(opts.componentName)}/prs${qs({ type: opts.types?.join(',') })}`,
    );
  }

  listPrsByGroup(opts: ListPrsByGroupOptions): Promise<GroupPrs> {
    return this.request<GroupPrs>(
      `${group(opts.groupName)}/prs${qs({ type: opts.types?.join(',') })}`,
    );
  }

  approvePr(
    componentName: string,
    prNumber: number,
  ): Promise<ApproveOrMergeResult> {
    return this.postAsUser<ApproveOrMergeResult>(
      `${comp(componentName)}/prs/${prNumber}/approve`,
    );
  }

  mergePr(
    componentName: string,
    prNumber: number,
    title?: string,
  ): Promise<ApproveOrMergeResult> {
    return this.postAsUser<ApproveOrMergeResult>(
      `${comp(componentName)}/prs/${prNumber}/merge`,
      { title },
    );
  }

  // Plain post: the skip only authorizes against Backstage credentials and
  // writes an audit line — no GitHub action runs, so no GitHub token needed.
  skipDeploy(
    componentName: string,
    body: { env: 'stg' | 'prd'; wave: number; state: string; reason: string },
  ): Promise<{ ok: true }> {
    return this.post<{ ok: true }>(`${comp(componentName)}/deploy-skip`, body);
  }

  triggerCherryPick(
    componentName: string,
    prNumber: number,
    to: string,
  ): Promise<{ ok: true }> {
    return this.post<{ ok: true }>(`${comp(componentName)}/cherrypick`, {
      prNumber,
      to,
    });
  }

  triggerPromote(
    componentName: string,
  ): Promise<{ ok: true; workflow?: string }> {
    return this.post<{ ok: true; workflow?: string }>(
      `${comp(componentName)}/promote`,
    );
  }

  triggerHotfix(
    componentName: string,
    stage: 'stg' | 'prd',
  ): Promise<{ ok: true; workflow?: string }> {
    return this.post<{ ok: true; workflow?: string }>(
      `${comp(componentName)}/hotfix`,
      { stage },
    );
  }

  getBuildStatus(componentName: string, since?: string): Promise<BuildStatus> {
    return this.request<BuildStatus>(
      `${comp(componentName)}/build-status${qs({ since })}`,
    );
  }

  getDeployStatus(
    componentName: string,
    env: 'stg' | 'prd',
    since?: string,
  ): Promise<DeployStatus> {
    return this.request<DeployStatus>(
      `${comp(componentName)}/deploy-status${qs({ env, since })}`,
    );
  }

  async getGroupBuildStatuses(
    groupName: string,
    componentNames: string[],
    since?: string,
  ): Promise<Record<string, BuildStatus>> {
    const data = await this.request<{
      statuses: Record<string, BuildStatus>;
    }>(
      `${group(groupName)}/build-status${qs({
        components: componentNames.length
          ? componentNames.join(',')
          : undefined,
        since,
      })}`,
    );
    return data.statuses;
  }

  async getGroupDeployStatuses(
    groupName: string,
    componentNames: string[],
    env: 'stg' | 'prd',
    since?: string,
  ): Promise<Record<string, DeployStatus>> {
    const data = await this.request<{
      statuses: Record<string, DeployStatus>;
    }>(
      `${group(groupName)}/deploy-status${qs({
        env,
        components: componentNames.length
          ? componentNames.join(',')
          : undefined,
        since,
      })}`,
    );
    return data.statuses;
  }

  getGroupE2eResults(groupName: string): Promise<GroupE2eResults> {
    return this.request<GroupE2eResults>(`${group(groupName)}/e2e-results`);
  }

  async getPr(componentName: string, prNumber: number): Promise<PrSummary> {
    const data = await this.request<{ pr: PrSummary }>(
      `${comp(componentName)}/prs/${prNumber}`,
    );
    return data.pr;
  }

  async getPrFiles(
    componentName: string,
    prNumber: number,
  ): Promise<PrFileDiff[]> {
    const data = await this.request<{ files: PrFileDiff[] }>(
      `${comp(componentName)}/prs/${prNumber}/files`,
    );
    return data.files;
  }

  async getPrPlans(
    componentName: string,
    prNumber: number,
  ): Promise<TerraformPlan[]> {
    const data = await this.request<{ plans: TerraformPlan[] }>(
      `${comp(componentName)}/prs/${prNumber}/plan`,
    );
    return data.plans;
  }
}
